<template>
    <Section id="pricing" class="bg-gray-200">
        <template #section>
            {{ $t('budgets_landing_page.pricing') }}
        </template>
        <template #title>
            {{ $t('budgets_landing_page.pricing_subtitle') }}
        </template>
        <div class="lg:grid-cols-3 gap-x-12 gap-y-8 lg:gap-y-12 grid grid-cols-1">
            <div v-for="subscriptionPlan in subscriptionPlans" class="p-6 bg-white border rounded-lg shadow-sm">
                <h3 class="my-0 text-xl font-semibold leading-8 text-gray-700">
                    {{ subscriptionPlan.name }}
                </h3>
                <p class="mt-3 mb-0 text-sm">
                    {{ subscriptionPlan.description }}
                </p>
                <p class="gap-x-1 flex items-baseline mt-6 mb-0">
                    <span class="text-4xl font-medium tracking-tight text-gray-800">
                        &euro;{{ subscriptionPlan.price }}
                    </span>
                    <span class="text-gray-body-400 text-sm font-semibold">
                        /{{ $t('organization_payments.month') }}
                    </span>
                </p>
                <ul role="list" class="mt-8 mb-0 space-y-3 text-sm">
                    <li class="gap-x-3 flex items-center">
                        <svg class="text-budgets-success-500 flex-none w-5 h-6" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                        </svg>
                        <template v-if="subscriptionPlan.max_user_limit">
                            {{ `${subscriptionPlan.max_user_limit} ${$t('organization_payments.users')}` }}
                        </template>
                        <template v-else>
                            {{ `${$t('organization_payments.unlimited_male')} ${$t('organization_payments.users')}` }}
                        </template>
                    </li>
                    <li class="gap-x-3 flex items-center">
                        <svg class="text-budgets-success-500 flex-none w-5 h-6" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                        </svg>
                        <template v-if="subscriptionPlan.max_main_entity_limit">
                            {{ `${subscriptionPlan.max_main_entity_limit} ${$t('budgets_landing_page.price_main_entity')}` }}
                        </template>
                        <template v-else-if="! subscriptionPlan.max_main_entity_limit">
                            {{ `${$t('organization_payments.unlimited_female')} ${$t('budgets_landing_page.price_main_entity')}` }}
                        </template>
                    </li>
                    <li class="gap-x-3 flex items-center">
                        <svg class="text-budgets-success-500 flex-none w-5 h-6" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                        </svg>
                        <template v-if="subscriptionPlan.support_type === 'basic'">
                            {{ $t('budgets_landing_page.basic_support') }}
                        </template>
                        <template v-else-if="subscriptionPlan.support_type === 'priority'">
                            <span v-html="$t('budgets_landing_page.priority_support', {
                                emphasizedOpen: '<span class=\'font-semibold\'>',
                                emphasizedClose: '</span>',
                            })"></span>
                        </template>
                    </li>
                    <li class="gap-x-3 flex items-center">
                        <template v-if="subscriptionPlan.can_share_projects">
                            <svg class="text-budgets-success-500 flex-none w-5 h-6" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                            </svg>
                            {{ $t('budgets_landing_page.share_projects') }}
                        </template>
                        <template v-else>
                            <svg class="text-gray-body-lighter flex-none w-5 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M4 10a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H4.75A.75.75 0 0 1 4 10Z" clip-rule="evenodd" />
                            </svg>
                            <span class="text-gray-body-lighter">
                                {{ $t('budgets_landing_page.share_projects') }}
                            </span>
                        </template>
                    </li>
                    <li class="gap-x-3 flex items-center">
                        <template v-if="subscriptionPlan.can_have_training_sessions">
                            <svg class="text-budgets-success-500 flex-none w-5 h-6" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                            </svg>
                            {{ $t('budgets_landing_page.training') }}
                        </template>
                        <template v-else>
                            <svg class="text-gray-body-lighter flex-none w-5 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M4 10a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H4.75A.75.75 0 0 1 4 10Z" clip-rule="evenodd" />
                            </svg>
                            <span class="text-gray-body-lighter">
                                {{ $t('budgets_landing_page.training') }}
                            </span>
                        </template>
                    </li>
                </ul>
            </div>
        </div>
        <div class="flex flex-col items-center justify-center mt-16">
            <div>
                <a href="/sign-up" class="hover:bg-blue-500 hover:text-white focus:no-underline hover:no-underline px-4 py-2 text-base font-medium leading-6 text-blue-500 bg-white border border-blue-500 rounded shadow-sm">
                    {{ $t('budgets_landing_page.start_free_trial') }}
                </a>
            </div>
            <p class="text-gray-body-400 mt-7 mb-0 text-sm" v-html="$t('budgets_landing_page.custom_plan_cta', {
                linkOpen: '<a href=&quot;/schedule-a-call&quot; class=&quot;text-blue-500&quot;>',
                linkClose: '</a>',
            })"></p>
        </div>
    </Section>
</template>

<script>
import Section from '../../../../shared/section.vue';

export default {
    components: {
        Section,
    },

    computed: {
        subscriptionPlans() {
            const subscriptionPlans = [
                {
                    name: this.$t('budgets_landing_page.price_1_name'),
                    description: this.$t('budgets_landing_page.price_1_description'),
                    price: 29,
                    max_user_limit: 3,
                    max_main_entity_limit: 25,
                    support_type: 'basic',
                    can_share_projects: false,
                    can_have_training_sessions: false,
                },
                {
                    name: this.$t('budgets_landing_page.price_2_name'),
                    description: this.$t('budgets_landing_page.price_2_description'),
                    price: 49,
                    max_user_limit: 10,
                    max_main_entity_limit: 45,
                    support_type: 'basic',
                    can_share_projects: false,
                    can_have_training_sessions: false,
                },
                {
                    name: this.$t('budgets_landing_page.price_3_name'),
                    description: this.$t('budgets_landing_page.price_3_description'),
                    price: 199,
                    max_user_limit: 50,
                    max_main_entity_limit: 250,
                    support_type: 'priority',
                    can_share_projects: true,
                    can_have_training_sessions: true,
                },
            ];

            return subscriptionPlans;
        },
    },
};
</script>
